import { useState } from 'react'

import OrderName from 'ui/order/OrderName'

import { useUpdateDesignName } from '@/lib/graphql/realthread/mutations'

import { useCartItem } from '@/context/CartItemContext'
import EstimatedDeliveryDate from '@/components/EstimatedDeliveryDate'
import type { GlobalDocument } from 'types/types.generated'

type Props = {
	readOnly?: boolean
	renderDate?: boolean
	globalBuilderData?: GlobalDocument
}

export default function CartItemNameAndDate({
	readOnly = false,
	renderDate = false,
	globalBuilderData = null
}: Props) {
	const { cartItem } = useCartItem()

	const [isEditingName, setIsEditingName] = useState<boolean>(false)
	const [loading, setLoading] = useState<boolean>(false)
	const [errorMessage, setErrorMessage] = useState<string>(null)
	const [openEditOrderDetails, setOpenEditOrderDetails] = useState<boolean>(false)
	const [ModalComponent, setModalComponent] = useState(null)

	const { updateDesignName } = useUpdateDesignName()

	const handleUpdateName = async (values) => {
		setLoading(true)

		try {
			await updateDesignName({
				variables: {
					token: cartItem.token,
					name: values.designName
				}
			})
			setErrorMessage(null)
			setIsEditingName(false)
		} catch (error) {
			setErrorMessage(error.message)
		} finally {
			setLoading(false)
		}
	}

	const handleOpenModal = async () => {
		const { ModalEditOrderDetails } = await import('../modals/ModalEditOrderDetails')
		setModalComponent(() => ModalEditOrderDetails)
		setOpenEditOrderDetails(true)
	}

	return (
		<div className="flex max-sm:flex-col flex-row items-end justify-between mt-6">
			<OrderName
				designName={cartItem.name}
				errorMessage={errorMessage}
				isEditingName={isEditingName}
				setIsEditingName={setIsEditingName}
				loading={loading}
				updateName={handleUpdateName}
				readOnly={readOnly}
				renderLabel
			/>

			{renderDate ? (
				<div className="flex flex-col gap-2 md:justify-end mb-6">
					<span className="text-sm -mb-2.5 md:text-right text-g-500">Estimated delivery</span>
					<EstimatedDeliveryDate
						item={cartItem}
						page="cart"
						readOnly={readOnly}
						fontSize="text-2xl font-serif whitespace-nowrap"
					/>
				</div>
			) : (
				<button
					className="button button-sm button-outline mb-7 whitespace-nowrap px-4"
					onClick={handleOpenModal}
				>
					Edit order details
				</button>
			)}
			{!renderDate && openEditOrderDetails && ModalComponent ? (
				<ModalComponent
					show={openEditOrderDetails}
					setShow={setOpenEditOrderDetails}
					globalBuilderData={globalBuilderData}
				/>
			) : null}
		</div>
	)
}
